/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
/* eslint-disable strict */
((algolia) => {
  'use strict';

  const { instantsearch, algoliasearch, insightsClient } = algolia.externals;
  const { searchBox, stats, sortBy, clearRefinements, panel, hits, pagination, configure } = algolia.externals.widgets;
  const { connectCurrentRefinements } = algolia.externals.connectors;

  let firstRender = true;

  const collectionPageEnabled =
    algolia.is_collection_results_page &&
    algolia.config.instant_search_enabled_on_collection;

  if (!algolia.full_results && !algolia.is_collection_results_page) {
    /**
     * If we aren't on the search page and neither on a collection page,
     * then there's no need to proceed
     */
    return;
  }
  if (algolia.full_results) {
    /**
     * If we are on the search page and instant search isn't enabled,
     * then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled) {
      return;
    }
  } else if (algolia.is_collection_results_page) {
    /**
     * If we are on a collection page and instant search on collection isn't
     * enabled, then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled_on_collection) {
      return;
    }
  }

  /**
   * Array which will contain all filters to be applied while initiating the
   * search API call.
   */
  const searchFilters = [];

  let collectionFacetFilter = null;
  let collectionRulesContextValue = null;
  let collectionHandle = null;

  if (collectionPageEnabled) {
    const matches = window.location.pathname.match(/\/collections\/([^/]+)/i);
    collectionHandle = Boolean(matches) && matches.length === 2 ? matches[1] : null;

    if (algolia.config.collection_id_indexing) {
      collectionFacetFilter = algolia.current_collection_id
        ? `collection_ids:"${algolia.current_collection_id}"`
        : null;
    } else {
      collectionFacetFilter = `collections:"${collectionHandle}"`;
    }

    // Add the collection filter to the list of search filters
    searchFilters.push(collectionFacetFilter);

    collectionRulesContextValue = algolia.config.collection_id_query_rules
      ? algolia.current_collection_id
      : collectionHandle;
  }

  // Filters for stock policy
  let stockPolicyFilter = null;

  if (algolia.config.stock_policy) {
    if (algolia.config.stock_policy === 'allow') {
      /**
       * For 'allow', we don't need to add any filter as we want to continue
       * displaying all out of stock items.
       */
    } else if (algolia.config.stock_policy === 'deny') {
      // For 'deny' we will filter out all items based on inventory quantity
      stockPolicyFilter = 'inventory_quantity > 0';
    } else if (algolia.config.stock_policy === 'continue') {
      /**
       * For 'continue' we will filter on `inventory_available` attribute whose
       * value is dependent on:
       * `inventory_quantity > 0 OR inventory_policy == 'continue'`
       */
      stockPolicyFilter = 'inventory_available:true';
    }

    // Add the stock policy filter to the list of search filters
    if (stockPolicyFilter) {
      searchFilters.push(stockPolicyFilter);
    }
  }

  let results_selector = collectionPageEnabled
    ? algolia.config.collection_css_selector
    : algolia.config.results_selector;

  const activeSortOrders =
    collectionPageEnabled && algolia.collectionSortOrders
      ? algolia.collectionSortOrders
      : algolia.sortOrders;

  results_selector += ', .algolia-shopify-instantsearch';

  function getTrackedUiState(uiState) {
    const trackedUiState = {};

    Object.keys(uiState).forEach((k) => {
      if (k === 'configure' || k === 'query' || k === 'q') {
        return;
      }
      trackedUiState[k] = uiState[k];
    });
    return trackedUiState;
  }

  /**
   * List of URL params which are "allowed" by InstantSearch.
   * These parameters won't be overwritten when the search state is written
   * to the URL.
   */
  const ALLOWED_FOREIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  let foreignRouteState;

  function singleIndex(indexName) {
    return {
      /**
       * `stateToRoute` method transforms the `uiState` object from InstantSearch
       * into an object that will then be serialized to construct the URL.
       */
      stateToRoute: (uiState) => {
        const route = getTrackedUiState(uiState[indexName] || {});
        route.q = uiState[indexName].query;
        return { ...foreignRouteState, ...route };
      },

      /**
       * `routeToState` method transforms the route object into a `uiState` object
       * so that InstantSearch is initialized with the correct state from the
       * URL.
       */
      routeToState: (routeState) => {
        /**
         * Backup parameters from `routeState` into a different object
         * `foreignRouteState` so that we can re-inject them into the object
         * returned within `stateToRoute`.
         * This allows us to preserve the parameters specified in `ALLOWED_FOREIGN_PARAMS`
         * array.
         */
        if (!foreignRouteState) {
          foreignRouteState = ALLOWED_FOREIGN_PARAMS.reduce((acc, key) => {
            acc[key] = routeState[key];
            return acc;
          }, {});
        }

        const state = {};
        state[indexName] = getTrackedUiState(routeState || {});
        state[indexName].query = routeState.q;
        return state;
      },
    };
  }

  function getIndexName() {
    const type = WYATT.config.searchType || 'product';

    return `${algolia.config.index_prefix}${type}s`;
  }

  function getInstantSearchProductTemplateName() {
    const defaultTemplateName = 'instantSearchProductTemplate';

    if (collectionPageEnabled) {
      return defaultTemplateName;
    }

    const type = WYATT.config.searchType;

    switch (type) {
      case 'article':
        return 'instantSearchArticleTemplate';
      case 'page':
        return 'instantSearchPageTemplate';
      default:
        return defaultTemplateName;
    }
  }

  function getHitsCssClasses() {
    const type = WYATT.config.searchType || 'product';

    return {
      list: ['product-grid', `product-grid--${type}`, 'list-reset'],
      item: ['product-grid__item', 'product-grid__item--algolia']
    }
  }

  function getStatsText() {
    const type = WYATT.config.searchType || 'product';

    switch (type) {
      case 'article':
        return WYATT.t.browsingControls.articles;
      case 'page':
        return WYATT.t.browsingControls.pages;
      default:
        return WYATT.t.browsingControls.products;
    }
  }

  const instant = {
    colors: algolia.config.colors,
    distinct: Boolean(algolia.config.show_products),
    facets: {
      hidden:
        collectionPageEnabled && algolia.collectionHiddenFacets
          ? algolia.collectionHiddenFacets
          : algolia.hiddenFacets,
      shown:
        collectionPageEnabled && algolia.collectionShownFacets
          ? algolia.collectionShownFacets
          : algolia.shownFacets,
      list:
        collectionPageEnabled && algolia.collectionFacets
          ? algolia.collectionFacets
          : algolia.facets,
      widgets:
        collectionPageEnabled && algolia.collectionFacetsWidgets
          ? algolia.collectionFacetsWidgets
          : algolia.facetsWidgets,
    },
    hitsPerPage:
      collectionPageEnabled &&
      algolia.config.collections_full_results_hits_per_page
        ? algolia.config.collections_full_results_hits_per_page
        : algolia.config.products_full_results_hits_per_page,
    search: instantsearch({
      searchClient: algoliasearch(
        algolia.config.app_id,
        algolia.config.search_api_key
      ),
      indexName: getIndexName(),
      routing: {
        stateMapping: singleIndex(getIndexName()),
      },
      searchFunction: (searchFunctionHelper) => {
        // Set query parameters here because they're not kept when someone
        // presses the Back button if set in the `init` function of a custom widget
        const page = searchFunctionHelper.getPage();

        if (instant.distinct) {
          searchFunctionHelper.setQueryParameter('distinct', true);
        }

        // Assign any required filters
        if (searchFilters.length) {
          searchFunctionHelper.setQueryParameter(
            'filters',
            searchFilters.join(' AND ')
          );
        }

        // Assign any required `ruleContexts` which are required for query rules
        // targeting collection pages
        if (collectionPageEnabled) {
          // Collection page merchandising:
          // If we are on a collection page, `collectionRulesContextValue` is defined
          if (collectionRulesContextValue) {
            searchFunctionHelper.setQueryParameter('ruleContexts', [
              collectionRulesContextValue.toString(),
            ]);
          } else {
            searchFunctionHelper.setQueryParameter('ruleContexts', []);
          }
        }

        searchFunctionHelper.setPage(page);
        searchFunctionHelper.search();
      },
    }),
    selector: `${results_selector}, .algolia-shopify-instantsearch`,
    sortOrders: activeSortOrders
  };

  instant.search.client.addAlgoliaAgent('Shopify Integration');

  if (algolia.config.analytics_enabled) {
    // Connecting IS with the insights middleware
    instant.search.use(algolia.insightsMiddleware);
  }

  algolia.instantsearch = instant;

  if (collectionPageEnabled) {
    if (
      document.querySelectorAll(algolia.config.collection_css_selector)
        .length === 0
    ) {
      throw new Error(
        'Instant search CSS selector for collection page is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/advanced-customization/collection-search-page/#css-selector'
      );
    }
  } else if (
    document.querySelectorAll(algolia.config.results_selector).length === 0
  ) {
    throw new Error(
      'Instant search CSS selector is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/building-search-ui/instant-search/#css-selector'
    );
  }

  // Instantiating the main page
  instant.$results = document.querySelector(instant.selector);
  instant.$results.innerHTML = '';
  algolia.render(algolia.instantSearchMainTemplate, instant.$results, {
    facets: instant.facets.list,
    multipleSortOrders: activeSortOrders.length > 1,
  });

  instant.search.addWidgets([
    configure({
      hitsPerPage: instant.hitsPerPage,
      facetingAfterDistinct: false,
    }),
  ]);

  // Search input
  instant.search.addWidgets([
    searchBox({
      container: '[data-algolia-search-box]',
      placeholder: algolia.translations.searchForProduct,
      showReset: false,
      showSubmit: false,
    }),
  ]);

  // Stats
  instant.search.addWidgets([
    stats({
      container: '[data-browsing-controls-stats-append-point]',
      templates: {
        text(data, { html }){
          return algolia.instantSearchStatsTemplate(data, html, {
            text: getStatsText()
          })
        }
      }
    }),
  ]);

  // Sort orders
  if (activeSortOrders.length > 1) {
    instant.search.addWidgets([
      sortBy({
        container: '[data-browsing-controls-sort-append-point]',
        items: instant.sortOrders,
        cssClasses: {
          select: 'select'
        }
      })
    ]);
  }

  // Hidden facets
  const list = instant.facets.hidden.map((facet) => facet.name);

  instant.search.addWidgets([
    {
      getConfiguration: () => ({
          facets: list,
          disjunctiveFacets: list,
        }),
      init: () => {},
    },
  ]);

  // Create the render function
  const createDataAttributes = (refinement) => Object.keys(refinement)
      .map((key) => `data-${key}="${encodeURIComponent(refinement[key] || '')}"`)
      .join(' ');

  const renderListItem = (item) => item.refinements.map((refinement) => {
      const label = (refinement.attribute === 'price_range') ? algolia.facetDisplayFunctions.price_range(refinement.label) : refinement.label;

      return `
        <li class="current-filters__item" data-refinement='${JSON.stringify(refinement)}'>
          <a ${createDataAttributes(refinement)} class="current-filters__remove" data-remove-filter-button aria-label="${WYATT.t.filters.remove} ${label}">
            <svg class="current-filters__icon" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
            <path d="M14.783 13.778a.712.712 0 0 1-.5 1.222.712.712 0 0 1-.505-.217L8 9.005l-5.778 5.778a.712.712 0 1 1-1.005-1.005L6.995 8 1.217 2.222a.71.71 0 0 1 1.005-1.005L8 6.995l5.778-5.778a.711.711 0 1 1 1.005 1.005L9.005 8l5.778 5.778Z"></path>
            </svg>
            ${label}
          </a>
        </li>
      `
    }).join('');

  const renderCurrentRefinements = (renderOptions) => {
    const { items, refine, widgetParams } = renderOptions;

    widgetParams.container.innerHTML = `
      <current-filters class="current-filters" data-current-filters='${JSON.stringify(items)}'>
        <ul class="current-filters__list list-reset" aria-label="${WYATT.t.filters.currentFilters}">
          ${items.map(renderListItem).join('')}
        </ul>
      </current-filters>
    `;

    Array.from(widgetParams.container.querySelectorAll('[data-remove-filter-button]'))
      .forEach((element) => {
        element.addEventListener('click', (event) => {

          const item = Object.keys(event.currentTarget.dataset).reduce((acc, key) => {
            const itemData = {};

            itemData[key] = decodeURIComponent(event.currentTarget.dataset[key]);

            return algolia.assign({}, acc, itemData);
          }, {});

          refine(item);
        });
      });
  };

  // Create the custom widget
  const customCurrentRefinements = connectCurrentRefinements(
    renderCurrentRefinements
  );

  const hasRefinements = (helper, facetsList) => facetsList.some((f) => {
      const isConjunctiveFacetRefined =
        helper.state.isConjunctiveFacet(f.name) &&
        helper.state.isFacetRefined(f.name);
      const isDisjunctiveFacetRefined =
        helper.state.isDisjunctiveFacet(f.name) &&
        helper.state.isDisjunctiveFacetRefined(f.name);
      const isHierarchicalFacetRefined =
        helper.state.isHierarchicalFacet(f.name) &&
        helper.state.isHierarchicalFacetRefined(f.name);

      const numericRefinements = helper.state.getNumericRefinements(f.name);
      const isNumericFacetRefined = Object.keys(numericRefinements).some((operator) => numericRefinements[operator].length > 0);

      return (
        isNumericFacetRefined ||
        isConjunctiveFacetRefined ||
        isDisjunctiveFacetRefined ||
        isHierarchicalFacetRefined
      );
    });

  /**
   * Decides whether the given `facetName` has any facet values available in
   * the current result set by checking the available `facets`,
   * `disjunctiveFacets` and `hierarchicalFacets`.
   */
  const hasFacetValues = (resultState, facetName) => {
    const isFacetValueAvailable = resultState.facets.some((facet) => facet.name === facetName);
    const isDisjunctiveFacetValueAvailable = resultState.disjunctiveFacets.some(
      (facet) => facet.name === facetName
    );
    const isHierarchicalFacetValueAvailable = resultState.hierarchicalFacets.some(
      (facet) => facet.name === facetName
    );

    return (
      isFacetValueAvailable ||
      isDisjunctiveFacetValueAvailable ||
      isHierarchicalFacetValueAvailable
    );
  };

  const customCurrentRefinementsWithPanel = panel({
    hidden: (options) => !hasRefinements(options.helper, instant.facets.list),
  })(customCurrentRefinements);

  const clearRefinementsWithPanel = panel({
    hidden: (options) => !hasRefinements(options.helper, instant.facets.list),
  })(clearRefinements);

  instant.search.addWidgets([
    clearRefinementsWithPanel({
      container: document.querySelector('[data-clear-filters-append-point]'),
      templates: {
        resetLabel: WYATT.t.filters.clearAll,
      },
      cssClasses: {
        button: ['button-reset', 'filters__clear-button'],
        disabledButton: ['button-reset', 'filters__clear-button--disabled']
      }
    }),
    customCurrentRefinementsWithPanel({
      container: document.querySelector(
        '[data-current-filters-append-point]'
      )
    }),
  ]);

  /**
   * Setup the facets for search.
   * We are using a `panel` with a custom `hidden` function which will allow
   * us to hide the parent container of the facet if there are no facet values
   * available for that particular facet.
   */
  instant.facets.widgets.forEach((widget) => {
    const facetWithPanel = panel({
      hidden: (options) => {
        const hide = !hasFacetValues(options.results, widget.params.attribute);
        const displayStyle = hide ? 'none' : '';

        /**
         * Replacing any "." and "%" in the class name with "\." and "\%" so that querySelector
         * can work as expected. "." is supposed to be present for named tags
         * and metafield based facet names and "%" is present when the facet name has a space
         * in it because of the encoding.
         */
        const facetName = encodeURIComponent(widget.params.attribute).replace(/\./g, '\\.').replace(/%/g, '\\%');
        const parentContainerSelector = `.filter-group--${facetName}`;
        const parentContainer = document.querySelector(parentContainerSelector);

        if (parentContainer) {
          parentContainer.style.display = displayStyle;
        }

        return hide;
      },
    })(widget.widget);

    instant.search.addWidgets([facetWithPanel(widget.params)]);
  });

  // Hits
  instant.search.addWidgets([
    hits({
      container: '[data-product-grid-append-point]',
      templates: {
        item(hit, { html, components }) {
          return algolia[getInstantSearchProductTemplateName()](hit, html, components);
        },
        empty(results, { html }) {
          return algolia.instantSearchNoResultTemplate(html);
        }
      },
      transformItems: (items) => items.map( item => ({
          ...item,
          _distinct: instant.distinct,
          can_order:
            item.inventory_management !== 'shopify' ||
            item.inventory_policy === 'continue' ||
            item.inventory_quantity > 0,
          queryID: item.__queryID,
          productPosition: item.__position,
          index: instant.search.mainIndex.getIndexName()
        })),
      cssClasses: getHitsCssClasses()
    })
  ]);

  // Redirect to product on click
  instant.search.addWidgets([
    {
      init: () => {
        const container = document.querySelector('[data-product-grid-append-point]');

        container.addEventListener('click', (e) => {
          e.preventDefault();

          let el = e.target;
          let hit;

          do {
            if (el.matches('.ais-hit')) {
              hit = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!hit) {
            return;
          }

          const link = hit.querySelector('a').href;

          if (algolia.config.analytics_enabled) {

            // Retrieving userToken
            let userToken;
            insightsClient('getUserToken', null, (err, newUserToken) => {
              if (err) {
                window.console.error(err);
                return;
              }
              userToken = newUserToken;
            });

            const clickData = {
              userToken,
              index: hit.dataset.algoliaIndex,
              eventName: 'click',
              queryID: hit.dataset.algoliaQueryid,
              objectIDs: [hit.dataset.algoliaObjectid],
              positions: [Number(hit.dataset.algoliaPosition)],
            };

            // Send the click event
            insightsClient('clickedObjectIDsAfterSearch', clickData);
            algolia.saveForConversionTracking(clickData);
          }
          window.location.href = link;
        });
      },
    },
  ]);

  // No result actions
  instant.search.addWidgets([
    {
      init: (opts) => {
        const container = document.querySelector('[data-product-grid-append-point]');

        container.addEventListener('click', (e) => {
          let el = e.target;
          let clearFilters;

          do {
            if (el.matches('.ais-hit-empty--clear-filters')) {
              clearFilters = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearFilters) {
            return;
          }

          const { helper } = opts;

          helper.clearTags();
          instant.facets.list.forEach((facet) => {
            helper.clearRefinements(facet.name);
          });
          helper.search();
        });

        container.addEventListener('click', (e) => {
          let el = e.target;
          let clearInput;

          do {
            if (el.matches('.ais-hit-empty--clear-input')) {
              clearInput = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearInput) {
            return;
          }

          opts.helper.setQuery('').search();
          const input = document.querySelector('.ais-search-box--input');
          input.value = '';
          input.focus();
        });
      },
    },
  ]);

  // Pagination
  instant.search.addWidgets([
    pagination({
      container: '[data-pagination-append-point]',
      padding: 2,
      maxPages: 20,
      cssClasses: {
        root: ['pagination__wrapper', 'content-wrapper'],
        list: ['pagination__list', 'inline-list'],
        item: 'pagination__item',
        firstPageItem: 'pagination__item--first',
        lastPageItem: 'pagination__item--last',
        previousPageItem: 'pagination__item--previous',
        nextPageItem: 'pagination__item--next',
        selectedItem: 'pagination__item--current',
        disabledItem: 'pagination__item--disabled',
        link: 'pagination__node'
      }
    }),
  ]);

  // Main
  // This prevents a second request to be made to Algolia when the userToken (using cookies) gets set. The first request is sent only when the userToken is set.
  function checkUserToken(){
    if(!algolia.userTokenCheck) {
      window.setTimeout(checkUserToken, 10); /* this checks the flag every 10 milliseconds */
    } else {
      instant.search.start();
      firstRender = false;
    }
  }

  if (algolia.config.analytics_enabled && algolia.config.usertoken_with_cookies === 'enabled' && firstRender) {
    checkUserToken();
  } else {
    instant.search.start();
  }

  algolia.contentHide.parentNode.removeChild(algolia.contentHide);
})(window.algoliaShopify);
